import React, { useState, useRef, useEffect } from 'react';
import styles from './Dropdown.module.scss';
import classNames from 'classnames';

export interface DropdownOption {
  label: string;
  value: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  emptyMessage?: string;
  disabled?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  emptyMessage = 'No options available',
  disabled = false,
}): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect((): Unsubscribe => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div
      className={classNames(styles.dropdown, className)}
      ref={ref}
      tabIndex={0}
      onBlur={() => setOpen(false)}
    >
      <div
        className={styles.selected}
        onClick={() => setOpen(prev => !prev)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedOption ? (
          selectedOption.label
        ) : (
          <span className={styles.placeholder}>
            {options.length > 0 ? placeholder : emptyMessage}
          </span>
        )}
        {!disabled ? <span className={styles.arrow} /> : null}
      </div>
      {open && !disabled && (
        <ul className={styles.options} role="listbox">
          {options.map(opt => (
            <li
              key={opt.value}
              className={`${styles.option} ${opt.value === value ? styles.selectedOption : ''}`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              role="option"
              aria-selected={opt.value === value}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
